// modules/auth/ui.ts — <app-auth>
// Pantalla de login y estado de carga inicial.
//   <app-auth>               → pantalla de login
//   <app-auth mode="loading"> → spinner mientras se restaura la sesión persistida

import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';

type AuthView = 'login' | 'connecting' | 'loading';

export class AppAuth extends HTMLElement {
  static define() { customElements.define('app-auth', AppAuth); }

  connectedCallback() {
    const initial: AuthView = this.getAttribute('mode') === 'loading' ? 'loading' : 'login';
    this.render(initial);
    useBus('auth:signed-in', () => this.remove(), this);
    useBus('auth:error', ({ message }) => this.showError(message), this);
  }

  private styles(): string {
    return `
      <style>
        app-auth {
          position: fixed; inset: 0; z-index: var(--z-modal);
          display: flex; align-items: center; justify-content: center;
          padding: var(--space-6);
          background:
            radial-gradient(120% 80% at 50% -10%, var(--color-primary-subtle) 0%, transparent 55%),
            var(--color-bg);
        }
        .card {
          width: 100%; max-width: 360px;
          display: flex; flex-direction: column; align-items: center; text-align: center;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-xl);
          padding: var(--space-10) var(--space-8) var(--space-8);
        }
        .logo {
          width: 120px; height: 120px; border-radius: var(--radius-lg);
          box-shadow: var(--shadow-md); margin-bottom: var(--space-6);
        }
        .tagline {
          font-size: var(--text-sm); color: var(--color-text-muted);
          line-height: var(--leading-normal); margin: 0 0 var(--space-8);
          max-width: 26ch;
        }
        .btn-google {
          width: 100%;
          display: flex; align-items: center; justify-content: center; gap: var(--space-3);
          background: var(--color-surface-raised); border: 1px solid var(--color-border-strong);
          border-radius: var(--radius-md); padding: var(--space-4);
          font-family: var(--font-body); font-size: var(--text-base);
          font-weight: var(--font-weight-semibold); color: var(--color-text);
          cursor: pointer; transition: border-color var(--duration-fast), background var(--duration-fast);
        }
        .btn-google:hover { background: var(--color-surface); border-color: var(--color-primary); }
        .btn-google:disabled { opacity: .6; cursor: not-allowed; }
        .btn-google:focus-visible { outline: 2px solid var(--color-border-focus); outline-offset: 2px; }
        .error {
          color: var(--color-error); font-size: var(--text-sm);
          margin: var(--space-4) 0 0; min-height: 1.2em;
        }
        .features {
          width: 100%; margin-top: var(--space-8); padding-top: var(--space-6);
          border-top: 1px solid var(--color-border);
          display: flex; flex-direction: column; gap: var(--space-4);
        }
        .feature {
          display: flex; align-items: center; gap: var(--space-3);
          font-size: var(--text-sm); color: var(--color-text-muted); text-align: left;
        }
        .feature-icon {
          flex-shrink: 0; width: 32px; height: 32px;
          display: grid; place-items: center; font-size: 1rem;
          background: var(--color-primary-subtle); border-radius: var(--radius-md);
        }
        .spinner {
          width: 18px; height: 18px; border-radius: 50%;
          border: 2px solid var(--color-border); border-top-color: var(--color-primary);
          animation: spin 0.7s linear infinite;
        }
        .loading-wrap { display: flex; flex-direction: column; align-items: center; gap: var(--space-5); }
        .loading-wrap .spinner { width: 28px; height: 28px; border-width: 3px; }
        .loading-text { font-size: var(--text-sm); color: var(--color-text-muted); }
        @keyframes spin { to { transform: rotate(360deg); } }
      </style>
    `;
  }

  private render(view: AuthView) {
    if (view === 'loading') {
      this.innerHTML = `
        ${this.styles()}
        <div class="loading-wrap">
          <img class="logo" src="/logo1.webp" alt="Platik-IA" width="120" height="120">
          <div class="spinner"></div>
          <p class="loading-text">Cargando tu información…</p>
        </div>
      `;
      return;
    }

    const connecting = view === 'connecting';
    this.innerHTML = `
      ${this.styles()}
      <div class="card">
        <img class="logo" src="/logo1.webp" alt="Platik-IA" width="120" height="120">
        <p class="tagline">Tus finanzas personales, con inteligencia artificial</p>

        <button class="btn-google" id="btnGoogle" ${connecting ? 'disabled' : ''}>
          ${connecting
            ? '<div class="spinner"></div><span>Conectando…</span>'
            : `<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continuar con Google</span>`
          }
        </button>
        <p class="error" id="authError"></p>

        <div class="features">
          <div class="feature"><span class="feature-icon">🎤</span><span>Registrá gastos con tu voz</span></div>
          <div class="feature"><span class="feature-icon">☁️</span><span>Tus datos se guardan en tu Google Drive</span></div>
          <div class="feature"><span class="feature-icon">📊</span><span>Análisis inteligente de tus finanzas</span></div>
        </div>
      </div>
    `;

    this.querySelector('#btnGoogle')?.addEventListener('click', () => {
      this.render('connecting');
      const auth = getModule<{ signIn: () => Promise<void> }>('auth');
      auth?.signIn();
    });
  }

  private showError(message: string) {
    this.render('login');
    const el = this.querySelector('#authError');
    if (el) el.textContent = message;
  }
}
